/** Deferred pixel loading, on its own subpath so a layout can schedule it
 *  without pulling the pixel loader into its eager chunk. */

/** Runs `callback` once idle (or after `timeoutMs`) and returns a cancel.
 *  Safari has no `requestIdleCallback`, so it takes the macrotask fallback. */
function onIdle(callback: () => void, timeoutMs: number): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(callback, { timeout: timeoutMs });
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(callback, 0);
  return () => clearTimeout(id);
}

/** Short on purpose: a visitor who leaves before it fires is never reported. */
const LOAD_IDLE_TIMEOUT_MS = 500;

/** Once idle, calls `load` and hands the module to `run`. A failed load is a
 *  warning, never an unhandled rejection. The returned cancel also skips `run`
 *  for a load in flight, leaving the report to the next page's own call. Each
 *  caller passes its own literal `() => import("...")` so the bundler can
 *  split the chunk. */
export function loadWhenIdle<M>(
  load: () => Promise<M>,
  run: (module: M) => void,
): () => void {
  let cancelled = false;
  const cancelIdle = onIdle(() => {
    load()
      .then((module) => {
        if (!cancelled) run(module);
      })
      .catch((error: unknown) => {
        console.warn("[meta-pixel] failed to load", error);
      });
  }, LOAD_IDLE_TIMEOUT_MS);
  return () => {
    cancelled = true;
    cancelIdle();
  };
}
